/**
 * @module common/postcodeSearch
 * @description 챗봇 접수 초안 카드용 다음(카카오) 우편번호 검색 (AA-137).
 *
 * 관리자 웹 `RepairRequestRegistration.openPostcode()` 와 같은 팝업을 띄우되, 결과를 폼에 직접
 * 대입하지 않고 `onResolved` 콜백으로 넘긴다. 지역 해석은 전부 `areaResolver` 에 맡기므로
 * 주소 문자열이 코드가 되는 경로는 여기에도 없다 (2026-09-15 장애 회귀 방지).
 * `common/KakaoPostCode.js` 는 쓰지 않는다 — `this.form.zipcode` 를 하드코딩으로 대입하고
 * 지역 해석에 필요한 `sigunguCode`/`sigungu` 를 버린다.
 *
 * ★ 관리자 웹과의 의도적 차이 (AA-137 D1 — 개명 포기):
 *   마스터에 있는 시군구는 **마스터 원본 `subName` 을 그대로 쓰고 daum `sigungu` 로 덮지 않는다**.
 *   관리자 웹은 덮어써서 BE `applyRenamedSubName`(행정구역 개편 반영)을 발동시키지만, 챗봇은
 *   상담원이 대화 중 누르는 무인 경로라 마스터 302건의 라벨을 검토 없이 UPDATE 하는 통로가 되면
 *   안 된다. 챗봇은 **신설만 하고 개명은 하지 않는다**. 개명 동기화는 관리자 웹이 이미 담당한다.
 *   덕분에 daum 이 `sigungu` 를 빈 값으로 주는 세종특별자치시(36110)도 마스터 히트로 균일하게
 *   해결된다.
 *
 * 쓰는 daum 필드는 `zonecode` / `address` / `sigunguCode` / `sigungu` 넷뿐이다.
 *   - `data.sido` 는 쓰지 않는다 — 약칭·정식명이 섞여 있어 마스터 `main_name` 을 훼손한다.
 *   - `data.buildingName` 도 쓰지 않는다 — 상세주소는 접수 중복 판정(dedup) 키라 건물명이
 *     들어가면 같은 주소가 별도 행으로 갈라진다.
 */
import { getArea } from '@/api/domain/common'
import { resolveAreaBySigunguCode, resolveNewAreaFromPostcode } from '@/common/areaResolver'

/** 팝업 창 크기 — 관리자 웹 `openPostcode()` 와 같은 값 */
const POPUP_WIDTH = 500
const POPUP_HEIGHT = 500

/**
 * 진행 중이거나 완료된 지역 마스터 조회 Promise.
 * 카드마다 다시 부르지 않도록 모듈 스코프에 남기고, 실패하면 비워 다음 호출에서 재시도한다.
 */
let areaListPromise = null

/**
 * 지역 마스터를 불러온다. 두 번째 호출부터는 캐시된 Promise 를 공유한다.
 *
 * ⚠️ 실패 판정은 예외가 아니라 **반환값**으로 한다. `api/api.js` 의 `getApi` 는
 * `try { ... } catch { return false }` 라 절대 throw 하지 않고 `false` 를 반환한다.
 * try/catch 만 두면 `false` 가 캐시에 영구 저장돼 그 세션의 모든 카드에서 지역 해석이 죽는다.
 *
 * @returns {Promise<Array<object>>} `getArea()` 응답을 가공 없이. 실패 시 빈 배열
 */
export function loadAreaList() {
  if (!areaListPromise) {
    areaListPromise = (async () => {
      const res = await getArea()
      if (!Array.isArray(res)) {
        window.__APP_LOG__?.('postcodeSearch: getArea failed')
        areaListPromise = null
        return []
      }
      return res
    })()
  }
  return areaListPromise
}

/**
 * @typedef {object} PostcodeResult
 * @property {string} postalCode - daum `zonecode` (5자리 우편번호)
 * @property {string} address - daum `address` (도로명/지번 주소)
 * @property {{areaMainCode:string, areaSubCode:string, areaMainName:string, areaSubName:string}} area
 *   해석된 지역. 마스터에도 없고 신규 구성도 불가하면 `areaSubCode` 가 `''` 이므로,
 *   호출 측은 지역 코드를 전송하지 않고 안내 문구를 띄운다 (기존 경로 유지, 마스터 오염 0).
 * @property {boolean} isNewDistrict - 마스터에 없어 daum 값으로 새로 구성한 시군구인지.
 *   `true` 일 때만 호출 측이 시군구명을 실어 BE 마스터 신설을 유도한다.
 */

/**
 * 다음(카카오) 우편번호 팝업을 연다.
 *
 * ⚠️ **동기 함수여야 한다.** `.open()` 앞에 `await` 를 두면 사용자 제스처 태스크를 벗어나
 * 팝업 차단기에 막힌다. 지역 마스터 로드는 `oncomplete` 안에서 await 한다.
 *
 * 카드 안에 임베드하지 않고 별도 창을 쓰는 이유: `.aw-panel` 은 `overflow:hidden` 이고
 * 카드 내부 가용 폭이 244~364px 라 daum 권장 최소 폭(300px)이 잘린다.
 *
 * @param {function(PostcodeResult): void} onResolved - 주소 선택 완료 콜백
 * @returns {boolean} 팝업을 열었으면 true, daum 스크립트가 없으면 false
 */
export function openPostcodeSearch(onResolved) {
  if (!window.daum?.Postcode) return false

  const left = (window.screen.width - POPUP_WIDTH) / 2
  const top = (window.screen.height - POPUP_HEIGHT) / 2

  const postcode = new window.daum.Postcode({
    oncomplete: async (data) => {
      const areaList = await loadAreaList()
      const hit = resolveAreaBySigunguCode(data.sigunguCode, areaList)
      // 마스터에 있으면 마스터 원본을 그대로 쓴다(개명 없음 — D1). 없을 때만 daum 값으로 신규 구성한다.
      const area = hit.areaSubCode ? hit : resolveNewAreaFromPostcode(data.sigunguCode, data.sigungu, areaList)

      onResolved({
        postalCode: data.zonecode,
        address: data.address,
        area,
        isNewDistrict: !hit.areaSubCode && !!area.areaSubCode
      })
    }
  })

  postcode.open({
    popupName: 'awPostcodePopup',
    left,
    top
  })
  return true
}
